/**
 * ApplicationFrame
 * -----
 * Fits a same-origin application iframe (e.g. crmformulieren) to the height of
 * its own content, so the content is never clipped by a fixed iframe height.
 * Cross-origin iframes (YouTube, Power BI, ...) are left untouched.
 */
export default class ApplicationFrame {
  constructor(el) {
    this.el = el;
    this.resizeTimer = null;
    this.mutationTimer = null;
    this.observer = null;
    this.frameObserver = null;
    this.appliedHeight = null;
    this.submitButton = null;
    this.fit =this.fit.bind(this);
    this.onFrameLoad = this.onFrameLoad.bind(this);
    this.init();
  }

  init() {
    this.fit();
    // The embedded application renders asynchronously, so re-fit a few times after load.
    [300, 800, 1500].forEach((delay) => setTimeout(this.fit, delay));

    this.el.addEventListener("load", this.onFrameLoad);
    window.addEventListener("resize", () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.fit, 200);
    });
    this.observeContent();
    this.observeSubmit();
    this.observeNavigation();
    this.observeFrameHeight();
  }

  fit() {
    const doc = this.contentDocument();
    if (!doc) {
      return;
    }

    // The fallback measurement clears the height, which momentarily collapses the iframe and
    // can shift the page's scroll position (e.g. Chrome's scroll anchoring jumps to the footer).
    // Restore the scroll position synchronously afterwards so the collapse is never visible.
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    const height = this.measure(doc);
    if (height <= 0) {
      window.scrollTo(scrollX, scrollY);
      return;
    }

    this.appliedHeight = `${height}px`;
    this.el.style.height = this.appliedHeight;

    const container = this.el.closest(".iframe-block, .iframe-surrounded");
    if (container && container.style.height) {
      container.style.height = `${height}px`;
    }

    window.scrollTo(scrollX, scrollY);

    // A clicked form action that is no longer in the document means the form was submitted and
    // replaced (e.g. by its confirmation); validation errors keep the form and its button.
    if (this.submitButton && !this.submitButton.isConnected) {
      this.submitButton = null;
      this.scrollToFrameTop();
    }
  }

  scrollToFrameTop() {
    const frameRect = this.el.getBoundingClientRect();
    if (frameRect.top < 0) {
      window.scrollTo({ top: frameRect.top + window.scrollY - 20 });
    }
  }

  measure(doc) {
    // Applications with an #app root (e.g. crmformulieren): measure their content wrapper
    // (margin box). It does not depend on the iframe height, so no reset is needed and
    // validation messages are included. Other same-origin pages use the fallback below.
    const wrap = doc.querySelector("#app .c-section-wrap");
    if (wrap) {
      const style = doc.defaultView.getComputedStyle(wrap);
      const margin =
        (Number.parseFloat(style.marginTop) || 0) + (Number.parseFloat(style.marginBottom) || 0);
      const wrapHeight = Math.ceil(wrap.getBoundingClientRect().height + margin);
      // The app can render content next to the wrap (e.g. a header section above it), so never
      // measure less than the app root itself.
      const app = doc.getElementById("app");
      const appHeight = app
        ? Math.max(Math.ceil(app.getBoundingClientRect().height), app.scrollHeight)
        : 0;
      return Math.max(wrapHeight, appHeight);
    }

    // Fallback: clearing the height first lets the content reflow to its natural size. Some
    // applications tie their body height to the iframe height, so measuring without this
    // reset makes the height grow unbounded on every re-fit. Reading the layout forces the reflow.
    this.appliedHeight = "";
    this.el.style.height = "";
    this.el.getBoundingClientRect();

    const body = doc.body;
    const html = doc.documentElement;
    return Math.max(
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0,
      html ? html.scrollHeight : 0
    );
  }

  onFrameLoad() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.fit();
    this.observeContent();
    this.observeSubmit();
    this.observeNavigation();
  }

  /**
   * Remembers the last clicked action button of an embedded crmformulieren form, so the next fit
   * can bring the frame's top into view once the submitted form is replaced. Without this, the
   * restored scroll offset lands the visitor at the bottom of the shortened page.
   */
  observeSubmit() {
    const doc = this.contentDocument();
    if (!doc) {
      return;
    }
    doc.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest && event.target.closest(".c-form__actions button");
        if (button) {
          this.submitButton = button;
        }
      },
      true
    );
  }

  /**
   * Single-page applications (e.g. the toertochten zoekmotor) open a detail through the History
   * API instead of loading a new document, so no load event fires and fit() keeps the current
   * scroll offset, leaving the visitor at the bottom of the new page. Bring the frame's top into
   * view whenever the embedded application navigates to another path.
   */
  observeNavigation() {
    const win = this.el.contentWindow;
    if (!this.contentDocument() || win.__applicationFrameNavigation) {
      return;
    }
    win.__applicationFrameNavigation = true;

    let path = win.location.pathname;
    const onNavigate = () => {
      if (win.location.pathname !== path) {
        path = win.location.pathname;
        this.scrollToFrameTop();
      }
    };

    ["pushState", "replaceState"].forEach((method) => {
      const original = win.history[method];
      win.history[method] = function (...args) {
        const result = original.apply(this, args);
        onNavigate();
        return result;
      };
    });
    win.addEventListener("popstate", onNavigate);
  }

  observeContent() {
    if (this.observer) {
      return;
    }

    const doc = this.contentDocument();
    if (!doc || !doc.body) {
      return;
    }

    // Fires on every size change of the app root (errors appearing, steps switching, ...),
    // including changes that don't touch the DOM tree such as animations.
    const app = doc.getElementById("app");
    if (app) {
      this.observer = new ResizeObserver(() => this.fit());
      this.observer.observe(app);
      return;
    }

    // Other same-origin pages: keep reacting to DOM changes as before.
    this.observer = new MutationObserver((mutations) => {
      // Tooltip/popup mutations (e.g. Semantic UI's ".ui.popup") don't affect the
      // document's real height and shouldn't trigger a collapse/reflow of the iframe.
      const relevant = mutations.some((mutation) => {
        const node = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        return !(node && node.closest(".ui.popup"));
      });
      if (!relevant) {
        return;
      }
      clearTimeout(this.mutationTimer);
      this.mutationTimer = setTimeout(this.fit, 150);
    });
    this.observer.observe(doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  }

  /**
   * The embedded application polls its own content and writes a height onto this iframe that
   * leaves out whatever overflows its app container, clipping that content. Re-fitting as soon as
   * a height other than our own appears keeps the measured height the one that survives.
   */
  observeFrameHeight() {
    this.frameObserver = new MutationObserver(() => {
      if (this.el.style.height !== this.appliedHeight) {
        this.fit();
      }
    });
    this.frameObserver.observe(this.el, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  contentDocument() {
    try {
      // Accessing a cross-origin iframe's document throws; those keep their configured height.
      return this.el.contentWindow.document;
    } catch (error) {
      return null;
    }
  }
}
